import { Dialog } from "@headlessui/react";
import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import { v4 as uuid } from "uuid";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import config from "config/config";
import { updateUserInfo } from "service/userservice/updateUserInfo";
import { states } from "js/placeholderData/states";

import { phoneNumberFormatting } from "js/formatting";
import { useSelector } from "react-redux";
import { getLoggedInUserProfileDetail } from "js/utilities";
import {
  emailChangedCheck,
  phoneNumberChangedCheck,
  addressChangedCheck,
} from "./utils/validator";
import { Trans, useTranslation } from "react-i18next";

import FormInputError from "components/General/FormInputError";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import MaskedPhoneNumberInput from "components/General/Form/MaskedPhoneNumberInput";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";

export default function RELEASE_USERINFODIALOG({
  dialogState,
  setDialogState,
}) {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const { policies } = useSelector((state) => state.data);
  const UUID = uuid();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [updateUserInfoErrorMessage, setUpdateUserInfoErrorMessage] =
    useState("");

  const userInfo = getLoggedInUserProfileDetail(policies, user);
  const [generalPurposeDialogState, setGeneralPurposeDialogState] =
    useState(false);
  const [selectedState, setSelectedState] = useState({
    value: userInfo?.address?.state ?? "all",
  });
  const isLoggedInUserPrimaryOwner01 =
    userInfo?.person?.partyTypeCode === "OW" &&
    userInfo?.person?.personId === "01";

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    clearErrors,
    setError,
    formState: { errors },
    reset: resetFormBackToDefault,
  } = useForm({
    defaultValues: {
      emailAddress: user?.emailId ?? userInfo?.email?.address,
      addressLine1: userInfo?.address?.addressLine1,
      addressLine2: userInfo?.address?.addressLine2,
      city: userInfo?.address?.city,
      state: userInfo?.address?.state,
      postalCode: userInfo?.address?.postalCode.substring(0, 5),
      phoneNumber: phoneNumberFormatting(userInfo?.phone?.phoneNumber),
      mobilePhone: user?.mobile,
    },
  });

  async function submitUserData(formData) {
    if (step !== 1) return;

    // restrict CSR to submit
    if (csrUser?.userId) {
      alert(t("ReleaseUserInfoDialog-restrictCSRSubmitMessage"));
      setDialogState(false);
      return;
    }

    // set up change flag for BE to choose email template
    const emailChanged = emailChangedCheck(formData, user, userInfo);
    const phoneNumberChanged = phoneNumberChangedCheck(formData, userInfo);
    const addressChanged = addressChangedCheck(formData, userInfo);
    const mobileNumberChanged = formData?.mobilePhone !== user.mobile;

    if (
      !emailChanged &&
      !phoneNumberChanged &&
      !addressChanged &&
      !mobileNumberChanged
    ) {
      setUpdateUserInfoErrorMessage(
        t("ReleaseUserInfoDialog-userInfoErrorMessage")
      );
      return;
    }

    setUpdateUserInfoErrorMessage("");
    setLoading(true);

    await updateUserInfo({
      ...formData,
      // strip formatting style for lifeSys DB
      phonenumber: formData?.phoneNumber
        ? formData?.phoneNumber?.replace(/[^\d]/g, "")
        : "",
      mobilenumber: formData?.mobilePhone
        ? formData.mobilePhone.replace(/[^\d]/g, "")
        : user?.mobile,
      contractId: policies.map((p) => p.contractID),
      ownerType: `${userInfo.person.partyTypeCode}${userInfo.person.personId}`,
      userId: user.userId,
      phonenumberUpdated: isLoggedInUserPrimaryOwner01
        ? phoneNumberChanged
        : false,
      addressUpdated: addressChanged,
    })
      .then(() => {
        setLoading(false);
        setStep(2);
      })
      .catch((error) => {
        setGeneralPurposeDialogState(true);
        setDialogState(false);
        setLoading(false);
        console.error(error);
      });
  }
  return (
    <>
      <Dialog
        open={dialogState}
        onClose={() => {
          setDialogState(false);
          setUpdateUserInfoErrorMessage("");
          resetFormBackToDefault();
          setStep(1);
        }}
        className="fixed inset-0 top-[10%] z-20 overflow-y-auto"
      >
        <DialogBackdrop />
        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className="ui-default-dialog max-w-xl"
        >
          <Dialog.Title className="font-body text-xl font-bold text-left">
            {t("ReleaseUserInfoDialog-dialogTitle")}
          </Dialog.Title>
          <button
            className="ui-default-dialog-close"
            aria-label={t("ReleaseUserInfoDialog-dialogAriaLabel")}
            onClick={() => {
              setStep(1);
              setUpdateUserInfoErrorMessage("");
              resetFormBackToDefault();
              setDialogState(false);
            }}
          >
            <svg
              className="ui-icon h-3.5 w-3.5"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-close"></use>
            </svg>
          </button>
          <Dialog.Description>
            {t("ReleaseUserInfoDialog-dialogDescription")}
          </Dialog.Description>
          <hr />
          <EverlakeLoadingOverlay isLoading={loading}>
            {step === 1 && (
              <form onSubmit={handleSubmit(submitUserData)}>
                <div className="w-full mx-auto grid gap-4 grid-cols-1">
                  {updateUserInfoErrorMessage && (
                    <FormInputError errorMessage={updateUserInfoErrorMessage} />
                  )}
                  <h3>{t("ReleaseUserInfoDialog-form-importantInfo")}</h3>

                  <div>
                    <label htmlFor={`${UUID}-email`} className="block mb-1">
                      {t("ReleaseUserInfoDialog-form-emailLabel")}
                    </label>
                    <input
                      id={`${UUID}-email`}
                      type="text"
                      placeholder={t(
                        "ReleaseUserInfoDialog-form-emailPlaceholder"
                      )}
                      {...register("emailAddress", {
                        required: t(
                          "ReleaseUserInfoDialog-form-emailRequiredText"
                        ),
                        pattern: {
                          value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                          message: t("ReleaseUserInfoDialog-form-emailMessage"),
                        },
                      })}
                    />
                    {errors.emailAddress?.message && (
                      <FormInputError
                        id={`${UUID}-emailError`}
                        errorMessage={errors.emailAddress.message}
                      />
                    )}
                    {!isLoggedInUserPrimaryOwner01 && (
                      <div className="text-xxs text-theme-text-tertiary mt-1">
                        {t("ReleaseUserInfoDialog-form-portalUseOnlyText")}
                      </div>
                    )}
                  </div>

                  <div>
                    <label id={`${UUID}-phone`} className="block mb-1">
                      {t("ReleaseUserInfoDialog-form-phoneNumberLabel")}
                      {isLoggedInUserPrimaryOwner01 ? "*" : ""}
                    </label>
                    {/* format phone number before setting default value to prevent form validation glitch for format diff */}
                    <MaskedPhoneNumberInput
                      name="phoneNumber"
                      describedby={`${UUID}-phone`}
                      register={register}
                      setValue={setValue}
                      disabled={!isLoggedInUserPrimaryOwner01}
                      defaultValue={getValues("phoneNumber")}
                      required={isLoggedInUserPrimaryOwner01}
                    />
                    {errors.phoneNumber?.message && (
                      <FormInputError
                        errorMessage={errors.phoneNumber.message}
                      />
                    )}
                  </div>

                  <div>
                    <label id={`${UUID}-mobile`} className="block mb-1">
                      {t("ReleaseUserInfoDialog-form-mobileNumberLabel")}
                    </label>
                    <MaskedPhoneNumberInput
                      name="mobilePhone"
                      describedby={`${UUID}-mobile`}
                      register={register}
                      setValue={setValue}
                      defaultValue={getValues("mobilePhone")}
                      required={false}
                    />
                    {errors.mobilePhone?.message && (
                      <FormInputError
                        errorMessage={errors.mobilePhone.message}
                      />
                    )}
                  </div>
                  {config.ENABLE_NON_US_USER === "true" &&
                  userInfo?.address?.countryIsoCode !== "" ? (
                    <>
                      <div className="text-sm font-semibold text-theme-text-secondary">
                        <div>{userInfo?.address?.addressLine1}</div>
                        <div>{userInfo?.address?.addressLine2}</div>
                        <div>{userInfo?.address?.city}</div>
                        <div>{userInfo?.address?.state}</div>
                        <div>
                          {userInfo?.address?.postalCode.substring(0, 5)}
                        </div>
                        <div>{userInfo?.address?.countryIsoCode}</div>
                      </div>
                      <h3>
                        {t(
                          "ReleaseUserInfoDialog-form-changeAddressImportantInfo"
                        )}
                      </h3>
                    </>
                  ) : (
                    <>
                      <div>
                        <label
                          htmlFor={`${UUID}-address`}
                          className="block mb-1"
                        >
                          {t("ReleaseUserInfoDialog-form-addressLabel1")}
                        </label>
                        <input
                          id={`${UUID}-address`}
                          type="text"
                          {...register("addressLine1", {
                            required:
                              "Please enter street number and street name for your mailing address",
                          })}
                        />

                        {errors.addressLine1?.message && (
                          <FormInputError
                            errorMessage={errors.addressLine1.message}
                          />
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor={`${UUID}-address2`}
                          className="block mb-1"
                        >
                          {t("ReleaseUserInfoDialog-form-addressLabel2")}
                        </label>
                        <input
                          id={`${UUID}-address`}
                          type="text"
                          placeholder={t(
                            "ReleaseUserInfoDialog-form-addressPlaceholder"
                          )}
                          {...register("addressLine2")}
                        />

                        {errors.addressLine2?.message && (
                          <FormInputError
                            errorMessage={errors.addressLine2.message}
                          />
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div>
                          <label
                            htmlFor={`${UUID}-city`}
                            className="block mb-1"
                          >
                            {t("ReleaseUserInfoDialog-form-cityLabel")}
                          </label>
                          <input
                            id={`${UUID}-city`}
                            type="text"
                            {...register("city", {
                              required: t(
                                "ReleaseUserInfoDialog-form-cityRequiredText"
                              ),
                            })}
                          />

                          {errors.city?.message && (
                            <FormInputError
                              errorMessage={errors.city.message}
                            />
                          )}
                        </div>

                        <div>
                          <Controller
                            name="state"
                            control={control}
                            render={() => (
                              <RELEASING_AutoCompleteComboBox
                                labelName={t(
                                  "ReleaseUserInfoDialog-form-stateLabel"
                                )}
                                inputPlaceholderText={t(
                                  "ReleaseUserInfoDialog-form-statePlaceholder"
                                )}
                                options={[
                                  {
                                    value: "all",
                                    displayAs: "Select",
                                    selectedPolicyDisplayAs: "Select",
                                    isDisplayOnly: true,
                                  },
                                  ...states.map((state) => ({
                                    value: state.abbreviation,
                                    displayAs: state.name,
                                    selectedPolicyDisplayAs: state.name,
                                    isDisplayOnly: true,
                                  })),
                                ]}
                                selectedOption={selectedState}
                                setSelectedOption={(option) => {
                                  if (option?.value !== "all") {
                                    clearErrors("state");
                                    setValue("state", option.value);
                                  } else {
                                    setError("state", {
                                      message: t(
                                        "ReleaseUserInfoDialog-form-stateRequiredText"
                                      ),
                                    });
                                    setValue("state", "");
                                  }
                                  setSelectedState(option);
                                }}
                              />
                            )}
                            rules={{
                              required: t(
                                "ReleaseUserInfoDialog-form-stateRequiredText"
                              ),
                            }}
                          />

                          {errors.state?.message && (
                            <FormInputError
                              errorMessage={errors.state.message}
                            />
                          )}
                        </div>

                        <div>
                          <label htmlFor={`${UUID}-zip`} className="block mb-1">
                            {t("ReleaseUserInfoDialog-form-zipCodeLabel")}
                          </label>
                          <input
                            id={`${UUID}-zip`}
                            type="text"
                            {...register("postalCode", {
                              required: t(
                                "ReleaseUserInfoDialog-form-zipCodeRequiredText"
                              ),
                              pattern: {
                                value: /^\d{5}$/,
                                message: t(
                                  "ReleaseUserInfoDialog-form-zipCodeMessage"
                                ),
                              },
                            })}
                          />

                          {errors.postalCode?.message && (
                            <FormInputError
                              errorMessage={errors.postalCode.message}
                            />
                          )}
                        </div>
                      </div>
                      <h3>
                        {t(
                          "ReleaseUserInfoDialog-form-mailingAddressImportantInfo"
                        )}
                      </h3>
                    </>
                  )}
                </div>
                <div className="block text-right">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => setDialogState(false)}
                  >
                    <Trans
                      i18nKey="ReleaseUserInfoDialog-form-closeButton"
                      components={{ span: <span className="sr-only" /> }}
                    />
                  </button>
                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    <Trans
                      i18nKey="ReleaseUserInfoDialog-form-saveButton"
                      components={{ span: <span className="sr-only" /> }}
                    />
                  </button>
                </div>
              </form>
            )}

            {step === 2 && (
              <>
                <div className="p-4 mx-auto md:p-6">
                  <div className="text-xl text-center font-semibold">
                    <Trans
                      i18nKey="UserInfoDialog-confirmationMessage"
                      components={{ break: <br /> }}
                    />
                  </div>
                  <div className="text-center mt-4 md:mt-6">
                    <button
                      type="button"
                      className="ui-btn-secondary ui-btn-min-w mt-4"
                      onClick={() => {
                        setStep(1);
                        setDialogState(false);
                      }}
                    >
                      {t("ReleaseUserInfoDialog-form-closeText")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </EverlakeLoadingOverlay>
        </Dialog.Panel>
      </Dialog>
      <GeneralPurposeDialog
        state={generalPurposeDialogState}
        setState={setGeneralPurposeDialogState}
        title={t("ReleaseUserInfoDialog-generalPurposeDialog-title")}
        desc={t("ReleaseUserInfoDialog-generalPurposeDialog-desc")}
      />
    </>
  );
}
